import { useEffect, useRef, useState } from 'react';
import {
  GUIDELINE_ITEM_MAX_LENGTH,
  holdsSeveralGuidelineItems,
  parseGuidelineItems,
  serializeGuidelineItems,
} from './guideline-items';

export interface GuidelineRow {
  /** Stable React key and focus target; never stored. */
  id: string;
  text: string;
}

interface FocusRequest {
  id: string;
  /** Caret position; the end of the text when omitted. */
  caret?: number;
}

let rowSequence = 0;
const newRow = (text = ''): GuidelineRow => ({ id: `guideline-row-${++rowSequence}`, text });

/** Always at least one row, so an empty card is ready to type in. */
const rowsFrom = (value: string): GuidelineRow[] => {
  const items = parseGuidelineItems(value);
  return items.length > 0 ? items.map((item) => newRow(item)) : [newRow()];
};

const textsOf = (rows: readonly GuidelineRow[]) => rows.map((row) => row.text);
const clip = (text: string) => text.slice(0, GUIDELINE_ITEM_MAX_LENGTH);

/**
 * Row state behind the guidelines list editor.
 *
 * The rows live here, not in the stored string: an empty row being typed, or a
 * trailing space, has no place in `dos`/`donts` and is never emitted. Only a
 * change of the serialised value reaches `onChange`. A value arriving from
 * outside (template load, AI draft) that differs from what the rows serialise
 * to replaces them, which is how a generated " · " text lands as items.
 */
export const useGuidelineRows = (value: string, onChange: (value: string) => void) => {
  const [rows, setRows] = useState(() => rowsFrom(value));
  // Last value seen from the parent or emitted to it.
  const [synced, setSynced] = useState(value);
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const focusRequest = useRef<FocusRequest | null>(null);

  if (value !== synced) {
    setSynced(value);
    if (value !== serializeGuidelineItems(textsOf(rows))) setRows(rowsFrom(value));
  }

  useEffect(() => {
    const request = focusRequest.current;
    if (!request) return;
    focusRequest.current = null;
    const input = inputs.current.get(request.id);
    if (!input) return;
    input.focus();
    const caret = request.caret ?? input.value.length;
    input.setSelectionRange(caret, caret);
  });

  const commit = (next: GuidelineRow[], focus?: FocusRequest) => {
    setRows(next);
    focusRequest.current = focus ?? null;
    const serialized = serializeGuidelineItems(textsOf(next));
    if (serialized !== synced) {
      setSynced(serialized);
      onChange(serialized);
    }
  };

  const indexOf = (id: string) => rows.findIndex((row) => row.id === id);

  const registerInput = (id: string) => (element: HTMLInputElement | null) => {
    if (element) inputs.current.set(id, element);
    else inputs.current.delete(id);
  };

  const changeRow = (id: string, text: string) =>
    commit(rows.map((row) => (row.id === id ? { ...row, text: clip(text) } : row)));

  /** Enter: a new row below a filled one. On an empty row it does nothing. */
  const insertAfter = (id: string) => {
    const index = indexOf(id);
    if (index < 0 || rows[index].text.trim() === '') return;
    const row = newRow();
    commit([...rows.slice(0, index + 1), row, ...rows.slice(index + 1)], { id: row.id });
  };

  /** Removes a row and moves focus to its neighbour; the last row is only cleared. */
  const removeRow = (id: string) => {
    const index = indexOf(id);
    if (index < 0) return;
    if (rows.length === 1) {
      commit([{ ...rows[0], text: '' }], { id: rows[0].id });
      return;
    }
    const next = rows.filter((row) => row.id !== id);
    const neighbour = index > 0 ? next[index - 1] : next[0];
    commit(next, index > 0 ? { id: neighbour.id } : { id: neighbour.id, caret: 0 });
  };

  /** Backspace on an empty row removes it. Returns whether it was handled. */
  const removeIfEmpty = (id: string): boolean => {
    const index = indexOf(id);
    if (index < 0 || rows[index].text !== '' || rows.length === 1) return false;
    removeRow(id);
    return true;
  };

  /**
   * Spreads pasted text holding several items (lines or " · ") over rows,
   * starting at the caret of the row pasted into. Returns whether it was
   * handled; a single item is left to the browser.
   */
  const pasteInto = (id: string, pasted: string, selectionStart: number, selectionEnd: number) => {
    if (!holdsSeveralGuidelineItems(pasted)) return false;
    const index = indexOf(id);
    if (index < 0) return false;
    const items = parseGuidelineItems(pasted);
    const current = rows[index].text;
    const before = current.slice(0, selectionStart);
    const after = current.slice(selectionEnd);
    if (items.length === 0) return true;

    const texts = items.map((item, position) => {
      const head = position === 0 ? before : '';
      const tail = position === items.length - 1 ? after : '';
      return { text: clip(`${head}${item}${tail}`), caret: head.length + item.length };
    });
    const pastedRows = texts.map(({ text }, position) =>
      position === 0 ? { ...rows[index], text } : newRow(text)
    );
    const last = pastedRows[pastedRows.length - 1];
    commit([...rows.slice(0, index), ...pastedRows, ...rows.slice(index + 1)], {
      id: last.id,
      caret: Math.min(texts[texts.length - 1].caret, last.text.length),
    });
    return true;
  };

  /** The discreet add button: focuses a trailing empty row rather than adding a second one. */
  const addRow = () => {
    const last = rows[rows.length - 1];
    if (last.text.trim() === '') {
      // Nothing changes, so no render follows to honour a focus request.
      inputs.current.get(last.id)?.focus();
      return;
    }
    const row = newRow();
    commit([...rows, row], { id: row.id });
  };

  return {
    rows,
    registerInput,
    changeRow,
    insertAfter,
    removeRow,
    removeIfEmpty,
    pasteInto,
    addRow,
  };
};
